/**
 * Permission-denied "empty everything" sentinel.
 *
 * When an RTK Query call is denied centrally, the auto-wrapper substitutes
 * this sentinel into `data` so component conditions like
 * `data?.list?.length === 0`, `data?.items?.map(...)`, `data?.count` etc.
 * resolve to sensible empty values **without per-file changes**.
 *
 * Behavior summary:
 *   - Any property access (string key, deep chain) yields the sentinel itself.
 *   - `.length`                       -> 0
 *   - Iteration (`for..of`, spread)   -> safe (no throw); deep chains still empty
 *   - Array methods (map/filter/...)  -> bound to `[]` (returns empty)
 *   - Numeric coercion                -> 0
 *   - String coercion                 -> ''
 *   - JSON.stringify                  -> null
 *   - Calling like a function         -> returns sentinel
 *   - React/Promise duck-typing keys  -> undefined (avoids confusing React)
 *
 * Trade-offs (read carefully):
 *   - The sentinel is *truthy* (it's an object). Code like `if (data) { ... }`
 *     enters the branch. Inside the branch, all reads degrade gracefully to
 *     empty/zero/sentinel, so this is usually safe — but side effects within
 *     a truthy branch (e.g. setState calls) will still run.
 *   - Arithmetic like `data.count + 1` may produce string `"1"` (because
 *     `Symbol.toPrimitive` returns `''` for `'default'` hint). Prefer
 *     `data.count * 1 + 1` or call `registerPermissionDeniedData` for the
 *     endpoint to provide an exact shape.
 *
 * If any specific endpoint misbehaves, opt out via:
 *   registerPermissionDeniedData('endpointName', { ...explicit shape... });
 */

/** Identifiers we must NOT auto-return the sentinel for (React/Promise/etc.). */
const REACT_INTERNAL_KEYS = new Set([
  // Promise duck-typing — React `then`-able fast path
  'then',
  'catch',
  'finally',
  // React element internals
  '$$typeof',
  '_owner',
  '_store',
  'props',
  'type',
  'ref',
  'key',
  // React reconciler internals
  '__reactFiber$',
  '__reactProps$',
  '__reactInternalInstance$',
  // Common Reflect / structuredClone surfaces
  'constructor',
]);

const ARRAY_METHOD_NAMES = new Set([
  'map',
  'filter',
  'forEach',
  'reduce',
  'reduceRight',
  'find',
  'findIndex',
  'findLast',
  'findLastIndex',
  'every',
  'some',
  'includes',
  'indexOf',
  'lastIndexOf',
  'slice',
  'concat',
  'flat',
  'flatMap',
  'join',
  'entries',
  'keys',
  'values',
  'at',
  'sort',
]);

/** Stable singleton so `===` and React `key` comparisons remain consistent. */
function createDeniedSentinel() {
  // eslint-disable-next-line prefer-const
  let sentinel;

  const emptyIterator = () => [][Symbol.iterator]();

  const handler = {
    get(target, prop) {
      if (prop === 'length') return 0;

      if (prop === Symbol.iterator) return emptyIterator;
      if (prop === Symbol.asyncIterator) return undefined;
      if (prop === Symbol.toStringTag) return 'PermissionDeniedSentinel';

      if (prop === Symbol.toPrimitive) {
        return (hint) => (hint === 'number' ? 0 : '');
      }
      if (prop === 'toString') return () => '';
      if (prop === 'valueOf') return () => 0;
      if (prop === 'toJSON') return () => null;

      if (typeof prop === 'symbol') return undefined;
      if (REACT_INTERNAL_KEYS.has(prop)) return undefined;

      // Bind array methods to a real empty array so iteration semantics work
      if (ARRAY_METHOD_NAMES.has(prop)) {
        return Array.prototype[prop].bind([]);
      }

      // Object.keys/entries/values shouldn't enumerate anything
      if (prop === 'hasOwnProperty') return () => false;
      if (prop === 'propertyIsEnumerable') return () => false;

      // Default: chain — keeps deep access (`data.a.b.c.length`) working
      return sentinel;
    },

    has() {
      return false;
    },
    // Delegate to the function target so Proxy invariants hold (e.g. `prototype`
    // must appear in ownKeys). Without this, `{...data}` / Object.keys(data)
    // throws when RTK Query substitutes this sentinel for denied responses.
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      return Reflect.getOwnPropertyDescriptor(target, prop);
    },
    apply() {
      return sentinel;
    },
  };

  // Use a function as target so the proxy is callable for the rare case where
  // a component invokes `data.something()` — returns sentinel via `apply`.
  // `typeof sentinel` is then 'function', which is uncommon in render paths.
  sentinel = new Proxy(function permissionDeniedSentinel() {}, handler);
  return sentinel;
}

/**
 * Stable, frozen singleton. Compare identity with `=== PERMISSION_DENIED_DATA`
 * to detect substituted denial data in component code if ever needed.
 */
export const PERMISSION_DENIED_DATA = createDeniedSentinel();

/** Helper: is this value our denial sentinel (handles deep chains too)? */
export function isPermissionDeniedSentinel(value) {
  // Identity check works because we always return the same singleton instance
  return value === PERMISSION_DENIED_DATA;
}
